// tslint:disable variable-name
import { Breadcrumb, Icon } from 'semantic-ui-react';
import * as React from 'react';
import { tl } from 'common/services/i18nService';
import { SessionState } from 'common/store/session/reducer';
import * as styles from 'common/components/header/Header.scss';
import { isNullOrUndefined } from 'common/util/type/typeUtil';
import { decodeDisplayText } from 'common/util/text/textUtil';
import {
    hasCurrentUserPlannerPrivilege,
    shouldRestrictVisibilityOfGraderInfo,
} from '../../services/permissions/PermissionService';
import {
    Committee,
    getBelongedCommittees,
    getGradingType,
    gradingTypes,
    getCommitteeById,
    getCommitteeNameByID,
} from '../../services/committeeService';

import urlContextParametersService from '../../services/urlContextParametersService';
import routingService from '../../services/routingService';
import { AssessmentState, WorkflowPhase } from '../../store/assessment/reducer';
import { ContextState } from '../../store/context/reducer';
import { WorkflowState } from '../../store/context/types';
import * as gradingStyles from './Header.scss';

const { useEffect } = React;

interface Props {
    assessment: AssessmentState;
    context: ContextState;
    session: SessionState;
    openCommitteeOverlay?: () => void;
    openPhaseOverlay?: () => void;
}

export const HeaderLeft = (props: Props) => {
    const {
        assessment, context, session, openCommitteeOverlay, openPhaseOverlay,
    } = props;
    const {
        committees,
        assessmentRunTitle,
        isMultipleAttemptsChild,
        attemptNumber,
        workflowPhases,
        isGradingWorkflowsEnabledForTest,
        gradingWorkflowDefinition,
        gradingWorkflowPhases,
    } = assessment;
    const { selectedCommitteeId } = context;
    const { isStudent } = session.user;
    const belongsCommittees = getBelongedCommittees(committees);
    const committee = getCommitteeById(selectedCommitteeId);

    useEffect(() => {
        if (isStudent) {
            return;
        }
        const checkBelongsCommittees = () => {
            if (!(context.selectedCommitteeId > 0)) {
                if (belongsCommittees.length === 1) {
                    const firstCommittee: Committee = belongsCommittees[0];
                    urlContextParametersService.setSelectedCommitteeId(firstCommittee.committeeId, context.partyId);
                }

                // in workflow mode "show all" (-1) is a valid selection for graders too, so don't force a committee pick
                if (!isGradingWorkflowsEnabledForTest && !hasCurrentUserPlannerPrivilege()
                    && belongsCommittees.length > 1 && openCommitteeOverlay) {
                    openCommitteeOverlay();
                }
                // case: current grader was removed from selected committee
            } else if (isNullOrUndefined(committee)) {
                if (belongsCommittees.length > 0) {
                    const firstCommittee: Committee = belongsCommittees[0];
                    urlContextParametersService.setSelectedCommitteeId(firstCommittee.committeeId, firstCommittee.candidateIds[0]);
                } else if (selectedCommitteeId !== 0) {
                    urlContextParametersService.setSelectedCommitteeId(0, context.partyId);
                }
            }
        };

        checkBelongsCommittees();
    },
    [committee]);

    useEffect(() => {
        if (isStudent || routingService.hasCheckedWorkflowCommitteeOnInitialLoad()) {
            return undefined;
        }

        const isUserPlanner = hasCurrentUserPlannerPrivilege();
        const isGradingCompleted = context?.workflowInfo?.plannerState === WorkflowState.GRADING_COMPLETED;
        // planners who have finished grading keep the legacy "first committee with candidates" behavior;
        // everyone else (graders, and planners still grading) follows the active-phase rules below
        const usesActivePhaseSelection = !(isUserPlanner && isGradingCompleted);
        const isAwaitingActivePhase = usesActivePhaseSelection && isGradingWorkflowsEnabledForTest
            && workflowPhases?.length > 0 && selectedCommitteeId <= 0 && !gradingWorkflowPhases;

        // per-phase status loads asynchronously (separately from workflowPhases), wait for it
        if (isAwaitingActivePhase) {
            return undefined;
        }

        routingService.markWorkflowCommitteeCheckedOnInitialLoad();

        if (isUserPlanner && isGradingCompleted) {
            if (
                isGradingWorkflowsEnabledForTest && belongsCommittees.length > 1 && workflowPhases?.length > 0
                && (selectedCommitteeId <= 0)
            ) {
                const committeeWithCandidates = belongsCommittees.find((com: any) => com.candidateCount > 0);
                if (committeeWithCandidates && workflowPhases.some((phase) => phase.committeeId === committeeWithCandidates.committeeId)) {
                    urlContextParametersService.setSelectedCommitteeId(committeeWithCandidates.committeeId, committeeWithCandidates.candidateIds[0]);
                } else {
                    urlContextParametersService.setSelectedCommitteeId(-1, context.partyId);
                }
            }
        } else if (isGradingWorkflowsEnabledForTest && workflowPhases?.length > 0 && selectedCommitteeId <= 0) {
            const activePhaseStatus = gradingWorkflowPhases?.find((phase) => phase.status === 'IN_PROGRESS');
            const activePhaseDefinition = activePhaseStatus
                ? gradingWorkflowDefinition?.find((phase) => phase.phaseId === activePhaseStatus.phaseId)
                : undefined;
            const committeesInActivePhase = activePhaseDefinition
                ? workflowPhases.filter((phase) => phase.name === activePhaseDefinition.name)
                : [];

            if (committeesInActivePhase.length === 1) {
                // single group in the active phase: select it automatically
                urlContextParametersService.setSelectedCommitteeId(committeesInActivePhase[0].committeeId, context.partyId);
            } else if (committeesInActivePhase.length > 1) {
                // multiple groups in the active phase: select "Show all"
                urlContextParametersService.setSelectedCommitteeId(-1, context.partyId);
            }
        }

        return () => {
            routingService.clearWorkflowCommitteeCheckedOnInitialLoad();
        };
    }, [gradingWorkflowPhases]);

    useEffect(() => {
        const clearWorkflowInitFlag = () => {
            routingService.clearWorkflowCommitteeCheckedOnInitialLoad();
        };

        if (!isStudent) {
            window.addEventListener('pagehide', clearWorkflowInitFlag);
            window.addEventListener('beforeunload', clearWorkflowInitFlag);
        }

        return () => {
            window.removeEventListener('pagehide', clearWorkflowInitFlag);
            window.removeEventListener('beforeunload', clearWorkflowInitFlag);
            clearWorkflowInitFlag();
        };
    }, []);

    const getCustomCommitteeNameByID = (committeeId: number): string => {
        const committeeName = getCommitteeNameByID(committeeId) || tl('grading.committees.selector.header.allCommittees');
        return decodeDisplayText(committeeName);
    };

    const getCommitteeName = (): string => {
        if (selectedCommitteeId > 0 || selectedCommitteeId === -1) {
            return getCustomCommitteeNameByID(selectedCommitteeId);
        }
        return belongsCommittees?.[0]?.name
            ? tl(`grading.committees.selector.header.${belongsCommittees?.[0]?.name}`)
            : '';
    };

    const getSelectedPhaseDetail = (key: keyof WorkflowPhase): string => {
        const selectedPhase = assessment.workflowPhases?.find((phase) => phase.committeeId === selectedCommitteeId);
        return selectedPhase ? String(selectedPhase[key]) : tl('grading.phase.selector.header.allPhases');
    };

    const getCommitteeType = (): string => {
        if (selectedCommitteeId >= 0) {
            return committee?.type
                ? getGradingType(committee.type)
                : getGradingType(belongsCommittees?.[0]?.type || gradingTypes.GRADING);
        }
        return '';
    };

    const restrictVisibilityOfGraderInfo = shouldRestrictVisibilityOfGraderInfo();
    const committeeButtonClassName = `${styles.spacing} ${styles.committeeButton} ${styles.truncate} ${
        restrictVisibilityOfGraderInfo ? styles.disabled : ''
    }`;

    return (
        <div className={styles.headerLeft}>
            <h1 className={gradingStyles.noMargin}>
                <Breadcrumb size="small" className={styles.breadCrumb}>
                    <Breadcrumb.Section active>
                        <span className={styles.lightText}>
                            {tl('grading.header.test')}
                        </span>
                        <span className={`${styles.darkText} ${styles.spacing}`}>
                            {assessmentRunTitle}
                        </span>
                    </Breadcrumb.Section>
                    {!isStudent
                        && (
                            <React.Fragment>
                                {
                                    isMultipleAttemptsChild ? (
                                        <React.Fragment>
                                            <Breadcrumb.Divider className={styles.divider}>/</Breadcrumb.Divider>
                                            <Breadcrumb.Section active>
                                                {tl('grading.header.attemptNumber', { attemptNumber })}
                                            </Breadcrumb.Section>
                                        </React.Fragment>
                                    ) : null
                                }
                            </React.Fragment>
                        )}
                </Breadcrumb>
            </h1>
            {!isStudent && (
                <Breadcrumb size="small" className={styles.breadCrumb}>
                    {isGradingWorkflowsEnabledForTest
                        ? (
                            <React.Fragment>
                                <Breadcrumb.Section>
                                    <span className={styles.lightText}>
                                        {tl('grading.common.phase')}
                                    </span>
                                    <span className={`${styles.spacing} ${styles.darkText}`}>
                                        {getSelectedPhaseDetail('name')}
                                    </span>
                                    <span className={styles.pipeSeparator}>|</span>
                                    <span className={styles.lightText}>
                                        {tl('grading.breadcrumb.group')}
                                    </span>
                                    <span className={`${styles.spacing} ${styles.darkText}`}>
                                        {getSelectedPhaseDetail('committeeName')}
                                    </span>
                                </Breadcrumb.Section>
                                {workflowPhases?.length > 0 && (
                                    <React.Fragment>
                                        <span className={styles.divider} />
                                        <Breadcrumb.Section className={styles.spacing} onClick={openPhaseOverlay}>
                                            <Icon name="exchange" data-testid="exchange-icon" />
                                            <u className={styles.spacing}>{tl('grading.header.viewOrChangePhase')}</u>
                                        </Breadcrumb.Section>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )
                        : (
                            <React.Fragment>
                                <Breadcrumb.Section>
                                    <span className={styles.lightText}>
                                        {getCommitteeType()}
                                    </span>
                                    <span className={selectedCommitteeId > 0 && styles.spacing}>
                                        {(selectedCommitteeId !== 0 || committee)
                                    && (selectedCommitteeId === -1
                                        ? tl('grading.header.committee')
                                        : tl('grading.header.committee').toLowerCase())}
                                    </span>

                                    <button
                                        type="button"
                                        className={committeeButtonClassName}
                                        onClick={openCommitteeOverlay}
                                        disabled={restrictVisibilityOfGraderInfo}
                                    >
                                        {getCommitteeName()}
                                    </button>
                                </Breadcrumb.Section>
                                {belongsCommittees && belongsCommittees.length > 0 && !restrictVisibilityOfGraderInfo && (
                                    <React.Fragment>
                                        <span className={styles.divider} />
                                        <Breadcrumb.Section className={styles.spacing} onClick={openCommitteeOverlay}>
                                            <Icon name="exchange" data-testid="exchange-icon" />
                                            <u className={styles.spacing}>{tl('grading.header.viewOrChangeCommittee')}</u>
                                        </Breadcrumb.Section>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                </Breadcrumb>
            )}
        </div>
    );
};
